import React, { useCallback, useRef } from "react";
import { Command } from "cmdk";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  Users,
  FolderKanban,
  Target,
  Zap,
  LifeBuoy,
  Wrench,
  FileText,
  UserCircle,
  Truck,
  ClipboardCheck,
  BookOpen,
  Package,
  Receipt,
  Building,
  Bug,
  Landmark,
  Search,
  Loader2,
  ArrowRight,
  Compass,
  FlaskConical,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useGlobalSearch } from "../../hooks/useGlobalSearch";
import { usePageSearch } from "../../hooks/usePageSearch";
import { COLLECTION_META, hitDocId } from "../../config/typesense";
import { FLUID_LABELS, STATUS_LABELS } from "../FluidSamples/labels";
import { GeneratorPhotoPreview } from "../FieldService/gallery/GeneratorPhotoPreview";
import { useGenerator } from "../../hooks/useGenerator";
import { readGalleryPreview } from "../../lib/fieldService/galleryPreview";

// ---------------------------------------------------------------------------
// Icon map — maps COLLECTION_META.icon string → Lucide component
// ---------------------------------------------------------------------------
const ICON_MAP: Record<string, LucideIcon> = {
  Phone,
  Users,
  FolderKanban,
  Target,
  Zap,
  LifeBuoy,
  Wrench,
  FileText,
  UserCircle,
  Truck,
  ClipboardCheck,
  BookOpen,
  Package,
  Receipt,
  Building,
  Bug,
  Landmark,
  FlaskConical,
};

// ---------------------------------------------------------------------------
// Formatting helpers for search result display
// ---------------------------------------------------------------------------
function formatSearchDate(unixSeconds: unknown): string {
  if (typeof unixSeconds !== "number" || unixSeconds <= 0) return "";
  const d = new Date(unixSeconds * 1000);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(amount: unknown): string {
  if (amount == null) return "";
  const n = typeof amount === "number" ? amount : parseFloat(String(amount));
  if (isNaN(n)) return "";
  return "$" + n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Safely convert a value to string — returns "" for objects/arrays to prevent [object Object] */
function safeStr(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "object") return "";
  return String(v);
}

// ---------------------------------------------------------------------------
// Helpers — extract human-readable title and subtitle from a raw Typesense doc
// ---------------------------------------------------------------------------
function getTitle(collection: string, doc: Record<string, unknown>): string {
  const s = safeStr;
  switch (collection) {
    case "call_logs":
      return s(doc.callerName) || "Unknown Caller";
    case "contacts":
      return s(doc.fullName) || "Unknown Contact";
    case "projects": {
      const name = s(doc.clientName) || "Unknown Project";
      const installId = s(doc.installId);
      return installId ? `${name} (${installId})` : name;
    }
    case "leads":
      return s(doc.companyName) || s(doc.contactName) || "Unknown Lead";
    case "equipment":
      return (
        [s(doc.customerName), s(doc.generatorMake), s(doc.generatorModel)]
          .filter(Boolean)
          .join(" — ") || "Unknown Equipment"
      );
    case "support_cases":
      return [s(doc.caseNumber), s(doc.subject)].filter(Boolean).join(" · ") || "Unknown Case";
    case "service_tickets":
      return (
        [s(doc.appSheetServiceId), s(doc.customerName)].filter(Boolean).join(" · ") ||
        "Unknown Ticket"
      );
    case "fluid_samples": {
      const fluid = FLUID_LABELS[s(doc.fluidType) as keyof typeof FLUID_LABELS] ?? "Fluid";
      return `${fluid} sample ${s(doc.sampleId) || "(no sample ID yet)"}`;
    }
    case "quotes":
      return [s(doc.quoteNumber), s(doc.subject)].filter(Boolean).join(" · ") || "Unknown Quote";
    case "employees":
      return s(doc.fullName) || "Unknown Employee";
    case "assets":
      return s(doc.name) || "Unknown Asset";
    case "form_submissions":
      return s(doc.templateName) || "Unknown Form";
    case "price_book_items":
      return (
        [s(doc.partNumber), s(doc.description)].filter(Boolean).join(" · ") || "Unknown Item"
      );
    case "service_parts_list":
      return (
        [s(doc.partNumber), s(doc.partDescription)].filter(Boolean).join(" - ") || "Unknown Part"
      );
    case "qbo_customers":
      return s(doc.fullyQualifiedName) || s(doc.displayName) || s(doc.companyName) || "Unknown Customer";
    case "qbo_invoices":
      return (
        ["Invoice", s(doc.docNumber), s(doc.customerName) ? `— ${s(doc.customerName)}` : ""]
          .filter(Boolean)
          .join(" ") || "Unknown Invoice"
      );
    case "issue_reports":
      return [s(doc.issueCode), s(doc.title)].filter(Boolean).join(" ") || "Unknown Issue";
    case "investors":
      return s(doc.name) || "Unknown Investor";
    default:
      return s(doc.name) || s(doc.title) || "Unknown";
  }
}

function getSubtitle(collection: string, doc: Record<string, unknown>): string {
  const s = safeStr;
  const parts = (...values: unknown[]): string =>
    values.map(safeStr).filter(Boolean).join(" · ");

  switch (collection) {
    case "call_logs":
      return parts(formatSearchDate(doc.createdAt), doc.callerPhone, doc.branch, doc.status);
    case "contacts":
      return parts(doc.companyName, doc.email, doc.phone);
    case "projects": {
      const statusDate = formatSearchDate(doc.updatedAt);
      const statusWithDate = statusDate
        ? `${s(doc.status)} (${statusDate})`
        : s(doc.status);
      return parts(statusWithDate, doc.address);
    }
    case "leads":
      // No pipeline/stage here: the leads index declares `pipelineId`/`stageId`
      // (functions/src/search/schemas.ts) and nothing populates even those, so
      // `doc.pipeline` / `doc.stage` have always resolved to undefined and
      // rendered nothing. Dropping the dead reads rather than switching to the
      // *Id names — those are opaque ids, and printing a raw id in the subtitle
      // would be worse than the blank it produces today.
      return parts(doc.opportunityName, doc.phone, doc.address);
    case "equipment":
      return parts(doc.generatorSerialNumber, doc.address, doc.status);
    case "support_cases":
      return parts(formatSearchDate(doc.createdAt), doc.status, doc.priority, doc.requesterName);
    case "service_tickets":
      // generatorId first: after pasting a Generator ID it confirms which unit the ticket is for.
      return parts(doc.generatorId, formatSearchDate(doc.createdAt), doc.status, doc.technician, safeStr(doc.address));
    case "fluid_samples":
      return parts(
        doc.unitName,
        doc.customerName,
        doc.serviceTicketId,
        STATUS_LABELS[s(doc.status) as keyof typeof STATUS_LABELS] ?? doc.status,
      );
    case "quotes":
      return parts(doc.status, doc.customerName);
    case "employees":
      return parts(doc.title, doc.department, doc.status);
    case "assets":
      return parts(doc.status, doc.assignedToName, doc.vin);
    case "form_submissions":
      return parts(doc.status, doc.customerName, doc.submittedByName);
    case "price_book_items":
      return parts(doc.category, doc.supplier, doc.manufacturer);
    case "service_parts_list":
      return parts(doc.branch, doc.category, doc.binLocation);
    case "qbo_customers":
      return parts(
        formatCurrency(doc.balance),
        doc.email,
        doc.phone,
      );
    case "qbo_invoices":
      return parts(
        formatCurrency(doc.totalAmt),
        doc.balance != null ? `bal ${formatCurrency(doc.balance)}` : null,
        doc.eInvoiceStatus,
        doc.txnDate,
      );
    case "issue_reports":
      return parts(doc.status, doc.severity, doc.classification, doc.reportedByName);
    case "investors":
      return parts(doc.entityName, doc.email, doc.status);
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [query, setQuery] = React.useState("");
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, prioritized, rest, sectionLabel, isLoading, isFetching, isSettled, error } = useGlobalSearch(query, {
    perCollection: 5,
  });

  const pageResults = usePageSearch(query);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (!nextOpen) {
        // Small delay to avoid flicker when cmdk resets internal state
        setTimeout(() => setQuery(""), 200);
      }
    },
    [onOpenChange],
  );

  const handleSelect = useCallback(
    (route: string) => {
      navigate(route);
      handleOpenChange(false);
    },
    [navigate, handleOpenChange],
  );

  const showSpinner = isLoading || isFetching;

  const renderGroups = (groups: typeof prioritized) =>
    groups.map((group) => {
      const meta = COLLECTION_META[group.collection];
      if (!meta) return null;

      const CollectionIcon = ICON_MAP[meta.icon] ?? Search;

      return (
        <Command.Group
          key={group.collection}
          heading={
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-100">
              <CollectionIcon className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                {meta.label}
              </span>
              <span className="ml-auto text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                {group.found}
              </span>
            </div>
          }
        >
          {group.hits.map((hit, idx) => {
            const doc = hit.document as Record<string, unknown>;
            const id = hitDocId([meta.idField, ...(meta.idFallbackFields ?? [])], doc);
            const isCandidateHit = group.collection === "contacts" && doc.isCandidate === true;
            const route = isCandidateHit ? `/recruiting/candidates/${id}` : meta.route(id);
            const title = getTitle(group.collection, doc);
            const subtitle = getSubtitle(group.collection, doc);
            const key = `${group.collection}-${id || idx}`;

            return (
              <Command.Item
                key={key}
                value={key}
                onSelect={() => handleSelect(route)}
                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer
                  aria-selected:bg-blue-50
                  hover:bg-slate-50
                  transition-colors group border-l-2 border-transparent
                  aria-selected:border-l-blue-500"
              >
                {/* A unit with photos shows its primary photo in place of the icon. Display-only:
                    this dialog closes on an outside click, so a lightbox from here would close
                    with it; selecting the row opens the unit page and its full gallery. */}
                {group.collection === "equipment" && id ? (
                  <GeneratorSearchThumb generatorDocId={id} Fallback={CollectionIcon} />
                ) : (
                  <CollectionIcon className="w-4 h-4 text-slate-400 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                    {title}
                    {isCandidateHit && (
                      <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded">
                        Candidate
                      </span>
                    )}
                  </div>
                  {subtitle && (
                    <div className="text-xs text-slate-500 truncate">
                      {subtitle}
                    </div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-aria-selected:text-blue-500 shrink-0 opacity-0 group-hover:opacity-100 group-aria-selected:opacity-100 transition-opacity" />
              </Command.Item>
            );
          })}
        </Command.Group>
      );
    });

  return (
    <Command.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      shouldFilter={false}
      label="Global search"
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      // Backdrop click closes
      onClick={(e: React.MouseEvent) => {
        if (e.target === e.currentTarget) handleOpenChange(false);
      }}
    >
      {/*
        Visually hidden Dialog title + description — required by Radix Dialog 1.1+
        for screen-reader accessibility. cmdk's `label` prop only sets aria-label
        on the content; Radix still warns unless an explicit DialogTitle exists.
      */}
      <Dialog.Title className="sr-only">Global search</Dialog.Title>
      <Dialog.Description className="sr-only">
        Search calls, contacts, projects, leads, generators, and more across the workspace.
      </Dialog.Description>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm"
        aria-hidden
        onClick={() => handleOpenChange(false)}
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden">
        {/* Search input row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-300">
          {showSpinner ? (
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin shrink-0" />
          ) : (
            <Search className="w-5 h-5 text-slate-400 shrink-0" />
          )}
          <Command.Input
            ref={inputRef}
            value={query}
            onValueChange={setQuery}
            placeholder="Search calls, contacts, projects, leads, generators…"
            className="flex-1 bg-transparent text-base text-slate-900 placeholder-slate-400 outline-none"
            autoFocus
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 rounded border border-slate-200">
            ESC
          </kbd>
        </div>

        {/* Error banner */}
        {error && (
          <div className="px-4 py-3 bg-red-50 border-b border-red-200 text-xs text-red-700">
            <div className="font-semibold mb-1">Search error:</div>
            <div className="break-words">{error.message || String(error)}</div>
          </div>
        )}

        {/* Results */}
        <Command.List className="max-h-[60vh] overflow-y-auto overscroll-contain bg-white">
          {/* Empty state */}
          <Command.Empty className="py-12 text-center text-sm text-slate-500">
            {query.length < 2
              ? "Type at least 2 characters to search…"
              : error
                ? "Search failed — see error above."
                : isSettled
                  ? "No results found."
                  : "Searching…"}
          </Command.Empty>

          {/* --- Pages --- */}
          {pageResults.length > 0 && (
            <>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-600 bg-emerald-50/60 border-b border-emerald-100">
                Pages
              </div>
              <Command.Group>
                {pageResults.map((page) => (
                  <Command.Item
                    key={`page-${page.path}`}
                    value={`page-${page.path}`}
                    onSelect={() => handleSelect(page.path)}
                    className="flex items-center gap-3 px-4 py-2.5 cursor-pointer
                      aria-selected:bg-emerald-50
                      hover:bg-slate-50
                      transition-colors group border-l-2 border-transparent
                      aria-selected:border-l-emerald-500"
                  >
                    <Compass className="w-4 h-4 text-emerald-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {page.label}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {page.moduleIcon} {page.moduleName}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-emerald-400 group-aria-selected:text-emerald-600 shrink-0 opacity-0 group-hover:opacity-100 group-aria-selected:opacity-100 transition-opacity" />
                  </Command.Item>
                ))}
              </Command.Group>
            </>
          )}

          {/* --- In this module --- */}
          {prioritized.length > 0 && (
            <>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-blue-600 bg-blue-50/60 border-b border-blue-100">
                {sectionLabel}
              </div>
              {renderGroups(prioritized)}
            </>
          )}

          {/* --- Other results --- */}
          {rest.length > 0 && prioritized.length > 0 && (
            <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100">
              Other results
            </div>
          )}
          {renderGroups(rest)}
        </Command.List>

        {/* Footer — search time and total count */}
        {(data && data.totalFound > 0) || pageResults.length > 0 ? (
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            <span>
              {(data?.totalFound ?? 0) + pageResults.length} result{((data?.totalFound ?? 0) + pageResults.length) !== 1 ? "s" : ""}
              {pageResults.length > 0 && ` (${pageResults.length} page${pageResults.length !== 1 ? "s" : ""})`}
            </span>
            <span>{data?.searchTimeMs ? `${data.searchTimeMs}ms` : ""}</span>
          </div>
        ) : null}
      </div>
    </Command.Dialog>
  );
}

/**
 * A generator hit's primary gallery photo, or the collection icon when it has none. One or two
 * cached reads per generator hit (a second when the hit carries the business Generator ID and
 * the lookup falls back to a query by it); a query shows a handful, shared with the unit page.
 */
function GeneratorSearchThumb({ generatorDocId, Fallback }: { generatorDocId: string; Fallback: LucideIcon }) {
  const { data, error } = useGenerator(generatorDocId);
  if (error || !readGalleryPreview(data)) return <Fallback className="w-4 h-4 text-slate-400 shrink-0" />;
  return <GeneratorPhotoPreview generator={data} interactive={false} size="xs" />;
}
